import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import '../../DashboardHome/ai-quality.css';
import './aiQuality.css';

const QI = {
    check: (
        <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
            <path d="M3 7.5l2.5 2.5L11 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    spark: (
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
            <path d="M8 2l1.3 3.4L13 6.6 9.7 8 8 11.5 6.3 8 3 6.6l3.4-1.2L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
    ),
    refresh: (
        <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
            <path d="M11.5 3.5a5 5 0 10.8 4.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M11.5 1.5v2.5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    caret: (
        <svg viewBox="0 0 12 12" width="11" height="11" fill="none">
            <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    arrow: (
        <svg viewBox="0 0 12 12" width="11" height="11" fill="none">
            <path d="M2 6h7M6 3l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
};

const RISK = {
    terminology: { label: 'Terminology', sev: 'high' },
    tags: { label: 'Tag mismatch', sev: 'high' },
    untranslated: { label: 'Untranslated', sev: 'high' },
    length: { label: 'Length', sev: 'med' },
    fluency: { label: 'Fluency', sev: 'med' },
    number: { label: 'Number drift', sev: 'med' },
    formality: { label: 'Formality', sev: 'med' },
};

const bucketOf = (s, th) => {
    if ((s.risks || []).some((r) => RISK[r]?.sev === 'high')) return 'risk';
    if (s.score >= th && (s.risks || []).length === 0) return 'safe';
    return 'review';
};

const ScoreGauge = ({ score }) => {
    const band = score >= 90 ? 'hi' : score >= 80 ? 'mid' : 'lo';
    return (
        <div className={`aiq-gauge ${band}`}>
            <div className="bar"><span style={{ width: `${score}%` }} /></div>
            <span className="num">{score}</span>
        </div>
    );
};

const SegmentRow = ({ s, bucket, expanded, onToggle, onApprove, onOpen, done }) => (
    <div className={`aiq-row ${bucket} ${done ? 'done' : ''}`}>
        <div className="aiq-row-main" onClick={onToggle} role="button" tabIndex={0}>
            <span className="aiq-n">#{s.n}</span>
            <div className="aiq-texts">
                <span className="src">{s.src}</span>
                <span className="tgt">
                    {s.tgt ? (
                        <>
                            <span className="ai-tag">AI</span>{s.tgt}
                        </>
                    ) : (
                        <span className="empty">— no draft —</span>
                    )}
                </span>
            </div>
            <ScoreGauge score={s.score} />
            <div className="aiq-risks">
                {(s.risks || []).length === 0 ? (
                    <span className="aiq-risk clean">{QI.check} Clean</span>
                ) : (
                    s.risks.map((r) => (
                        <span key={r} className={`aiq-risk ${RISK[r]?.sev || 'med'}`}>{RISK[r]?.label || r}</span>
                    ))
                )}
            </div>
            <span className={`aiq-caret ${expanded ? 'open' : ''}`}>{QI.caret}</span>
        </div>
        {expanded && (
            <div className="aiq-why">
                <div className="aiq-why-h">{QI.spark} Why this score</div>
                <p>{s.why || (s.risks.length ? `Risks detected: ${s.risks.map((r) => RISK[r]?.label || r).join(', ')}.` : 'No risks detected; score reflects model confidence.')}</p>
                <div className="aiq-why-actions">
                    <button type="button" className="set-btn sm primary" onClick={onApprove}>
                        {QI.check} Approve
                    </button>
                    <button type="button" className="set-btn sm" onClick={onOpen}>
                        Open in editor {QI.arrow}
                    </button>
                </div>
            </div>
        )}
    </div>
);

const AIQuality = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [projects, setProjects] = useState([]);
    const [scopeProjectId, setScopeProjectId] = useState('');
    const [data, setData] = useState({ stats: {}, segments: [] });
    const [threshold, setThreshold] = useState(88);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [approved, setApproved] = useState({});

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/projects?summary=true', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                const { createdProjects = [], sharedProjects = [] } = r.data || {};
                const unique = new Map([...createdProjects, ...sharedProjects].map((p) => [p._id, p]));
                const list = [...unique.values()];
                setProjects(list);
            })
            .catch(() => {});
    }, [token]);

    useEffect(() => {
        if (!token) return;
        setLoading(true);
        const url = `/api/dashboard/ai-quality?threshold=${threshold}${scopeProjectId ? `&projectId=${scopeProjectId}` : ''}`;
        axios
            .get(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setData(r.data || { stats: {}, segments: [] });
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load AI quality.');
                    setLoading(false);
                }
            });
    }, [token, threshold, scopeProjectId, dispatch, navigate]);

    const buckets = useMemo(() => {
        const safe = [];
        const review = [];
        const risk = [];
        (data.segments || []).forEach((s) => {
            if (approved[`${s.projectId}-${s.n}`]) return;
            const b = bucketOf(s, threshold);
            if (b === 'safe') safe.push(s);
            else if (b === 'review') review.push(s);
            else risk.push(s);
        });
        return { safe, review, risk };
    }, [data.segments, threshold, approved]);

    const queue = useMemo(
        () => [...buckets.risk, ...buckets.review].sort((a, b) => a.score - b.score),
        [buckets],
    );

    const total = buckets.safe.length + buckets.review.length + buckets.risk.length;
    const stats = data.stats || {};
    const avgScore = stats.avgScore || 0;

    const pct = (n) => (total ? (n / total) * 100 : 0);

    const autoApprove = () => {
        const next = { ...approved };
        buckets.safe.forEach((s) => { next[`${s.projectId}-${s.n}`] = true; });
        setApproved(next);
    };

    const approveOne = (s) => setApproved((a) => ({ ...a, [`${s.projectId}-${s.n}`]: true }));

    const scopeLabel = useMemo(() => {
        if (scopeProjectId) {
            const p = projects.find((x) => x._id === scopeProjectId);
            return p ? `${p.name} · ${(p.sourceLanguage || 'EN').toUpperCase()} → ${(p.targetLanguage || '').toUpperCase()}` : 'Project';
        }
        return 'All projects';
    }, [scopeProjectId, projects]);

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading AI quality…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">AI Quality</h1>
                <div className="rs-page-actions">
                    <select
                        className="set-input"
                        value={scopeProjectId}
                        onChange={(e) => setScopeProjectId(e.target.value)}
                        style={{ height: 32 }}
                    >
                        <option value="">All projects</option>
                        {projects.map((p) => (
                            <option key={p._id} value={p._id}>{p.name}</option>
                        ))}
                    </select>
                    <span className="aiq-scope">{scopeLabel}</span>
                    <button type="button" className="btn-q" onClick={() => setThreshold(threshold)}>
                        {QI.refresh}<span>Re-run analysis</span>
                    </button>
                </div>
            </div>
            <p className="rs-page-sub">Rush AI scores every draft for adequacy, fluency, and risk before a human looks — so your team spends time only where it matters.</p>

            <div className="aiq-kpis">
                <div className="aiq-kpi"><div className="v">{avgScore}</div><div className="l">Avg quality estimate</div></div>
                <div className="aiq-kpi"><div className="v">{stats.totalSegs || 0}</div><div className="l">Segments analyzed</div></div>
                <div className="aiq-kpi good"><div className="v">{buckets.safe.length}</div><div className="l">Safe to auto-approve</div></div>
                <div className="aiq-kpi warn"><div className="v">{buckets.review.length + buckets.risk.length}</div><div className="l">Need your eyes</div></div>
            </div>

            <div className="rs-card aiq-triage">
                <div className="aiq-triage-h">
                    <div>
                        <h3>Triage</h3>
                        <span>Everything at or above your confidence bar with no high-severity risk is cleared for auto-approval.</span>
                    </div>
                    <button
                        type="button"
                        className="set-btn accent"
                        onClick={autoApprove}
                        disabled={buckets.safe.length === 0}
                        style={buckets.safe.length === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : null}
                    >
                        {QI.check} Auto-approve {buckets.safe.length} safe
                    </button>
                </div>
                <div className="aiq-dist">
                    <div className="seg safe" style={{ width: `${pct(buckets.safe.length)}%` }} title="Safe" />
                    <div className="seg review" style={{ width: `${pct(buckets.review.length)}%` }} title="Review" />
                    <div className="seg risk" style={{ width: `${pct(buckets.risk.length)}%` }} title="Risk" />
                </div>
                <div className="aiq-legend">
                    <span><i className="d safe" />Safe · {buckets.safe.length}</span>
                    <span><i className="d review" />Review recommended · {buckets.review.length}</span>
                    <span><i className="d risk" />High risk · {buckets.risk.length}</span>
                </div>
                <div className="aiq-th">
                    <span className="lbl">Auto-approve at or above</span>
                    <input
                        type="range"
                        className="set-slider"
                        min={70}
                        max={100}
                        step={1}
                        value={threshold}
                        onChange={(e) => setThreshold(Number(e.target.value))}
                    />
                    <span className="val">{threshold}%</span>
                </div>
            </div>

            <div className="aiq-queue-h">
                <h3>Review queue</h3>
                <span className="ct">{queue.length}</span>
                <span className="sub">riskiest first</span>
            </div>
            <div className="aiq-list">
                {queue.length === 0 && (
                    <div className="aiq-empty">{QI.check} Queue clear — every segment is cleared or approved. Nice.</div>
                )}
                {queue.map((s) => {
                    const key = `${s.projectId}-${s.n}`;
                    return (
                        <SegmentRow
                            key={key}
                            s={s}
                            bucket={bucketOf(s, threshold)}
                            expanded={expanded === key}
                            onToggle={() => setExpanded(expanded === key ? null : key)}
                            onApprove={() => approveOne(s)}
                            onOpen={() => navigate(`/editor/${s.projectId}`)}
                            done={!!approved[key]}
                        />
                    );
                })}
            </div>
        </>
    );
};

export default AIQuality;
